import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, FileText, Loader2, Sparkles, Undo2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useLocalization } from '@/hooks/useLocalization';
import { useUserRole } from '@/hooks/useUserRole';
import {
  useImportProposals,
  type ImportProposal,
  type ImportProposalType,
  type ProposalFieldKey,
  type ProposalOverrides,
} from '@/hooks/useImportProposals';

interface ImportReviewStripProps {
  type: ImportProposalType;
  groupBy?: 'portfolio';
  onChanged?: () => void;
  onOpenDocumentIntelligence?: () => void;
}

const CONFLICT_FIELDS: Array<Exclude<ProposalFieldKey, 'name' | 'description'>> = ['budget', 'start_date', 'end_date', 'portfolio', 'spent', 'progress', 'health'];
const NUMBER_FIELDS: ProposalFieldKey[] = ['budget', 'spent', 'progress'];
const HEALTH_OPTIONS = ['on-track', 'at-risk', 'critical'] as const;

// The proposal's own value for a field, before any reviewer edit.
function baseValue(p: ImportProposal, key: ProposalFieldKey): string | number | null {
  return key === 'name' ? p.name : p.fields[key];
}

function effectiveValue(p: ImportProposal, overrides: ProposalOverrides | undefined, key: ProposalFieldKey): string | number | null {
  return overrides && key in overrides ? overrides[key] ?? null : baseValue(p, key);
}

// A conflicted field stops needing attention once the reviewer has chosen a value for it.
function unresolvedConflicts(p: ImportProposal, overrides: ProposalOverrides | undefined): string[] {
  return Object.keys(p.conflicts).filter((k) => !(overrides && k in overrides));
}

function existingValue(p: ImportProposal, key: ProposalFieldKey): string | number | null {
  const m = p.matched_project;
  if (!m) return null;
  if (key === 'portfolio') return m.portfolio_id;
  // projects_sync stores timestamps; proposals carry plain dates.
  if (key === 'start_date' || key === 'end_date') return m[key] ? m[key]!.slice(0, 10) : null;
  return m[key];
}

export const ImportReviewStrip = ({ type, groupBy, onChanged, onOpenDocumentIntelligence }: ImportReviewStripProps) => {
  const { t, tx, currency } = useLocalization();
  const { canAccess } = useUserRole();
  const allowed = canAccess('documentIntelligence');
  const { proposals, leftoverCount, loading, busy, undoableBatchId, apply, dismiss, undo } = useImportProposals(type);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, ProposalOverrides>>({});
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');

  // Pre-tick confident proposals the reviewer has not touched; drop state for
  // proposals that no longer exist (rebuilt by a background consolidation).
  useEffect(() => {
    const ids = new Set(proposals.map((p) => p.id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      for (const p of proposals) {
        if (!touched.has(p.id) && !p.needs_attention) next.add(p.id);
      }
      return next;
    });
    setOverrides((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => ids.has(id))));
  }, [proposals, touched]);

  const readyCount = useMemo(() => proposals.filter((p) => !p.needs_attention).length, [proposals]);
  const attentionCount = proposals.length - readyCount;

  const sections = useMemo(() => {
    if (groupBy !== 'portfolio') return [{ label: null as string | null, items: proposals }];
    const map = new Map<string, ImportProposal[]>();
    for (const p of proposals) {
      const label = String(effectiveValue(p, overrides[p.id], 'portfolio') ?? '') || t('No portfolio');
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(p);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, items]) => ({ label, items }));
  }, [proposals, groupBy, overrides, t]);

  // What approving the ticked projects would add up to: the figures a reviewer
  // checks against their own portfolio report before approving.
  const rollup = useMemo(() => {
    if (type !== 'project') return null;
    const picked = proposals.filter((p) => selected.has(p.id));
    if (picked.length === 0) return null;
    const num = (p: ImportProposal, key: 'budget' | 'spent' | 'progress') => {
      const v = effectiveValue(p, overrides[p.id], key);
      return typeof v === 'number' ? v : v === null || v === undefined || v === '' ? null : Number(v);
    };
    let budget = 0;
    let spent = 0;
    let weighted = 0;
    let weightedBudget = 0;
    const health = { 'on-track': 0, 'at-risk': 0, critical: 0 };
    let rated = 0;
    const portfolios = new Set<string>();
    for (const p of picked) {
      const b = num(p, 'budget');
      const pr = num(p, 'progress');
      budget += b ?? 0;
      spent += num(p, 'spent') ?? 0;
      if (b && pr !== null && !Number.isNaN(pr)) {
        weighted += b * pr;
        weightedBudget += b;
      }
      const h = String(effectiveValue(p, overrides[p.id], 'health') ?? '');
      if (h in health) {
        health[h as keyof typeof health]++;
        rated++;
      }
      const portfolio = String(effectiveValue(p, overrides[p.id], 'portfolio') ?? '').trim();
      if (portfolio) portfolios.add(portfolio.toLowerCase());
    }
    return {
      count: picked.length,
      portfolios: portfolios.size,
      budget,
      spent,
      completion: weightedBudget > 0 ? Math.round((weighted / weightedBudget) * 10) / 10 : null,
      health: rated > 0 ? health : null,
    };
  }, [type, proposals, selected, overrides]);

  if (!allowed || loading) return null;
  if (proposals.length === 0 && !undoableBatchId) return null;

  const toggle = (id: string, on: boolean) => {
    setTouched((prev) => new Set(prev).add(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const setField = (id: string, key: ProposalFieldKey, raw: string) => {
    const value: string | number | null =
      raw.trim() === '' ? null : NUMBER_FIELDS.includes(key) ? Number(raw) : raw;
    if (NUMBER_FIELDS.includes(key) && value !== null && Number.isNaN(value)) return;
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  };

  const handleApply = async () => {
    const items = proposals
      .filter((p) => selected.has(p.id))
      .map((p) => ({ proposal_id: p.id, overrides: overrides[p.id] }));
    if (items.length === 0) return;
    const result = await apply(items);
    if (result.ok === false) {
      toast.error(result.error);
      return;
    }
    toast.success(t('{0} item(s) approved and added', { 0: items.length }));
    setTouched(new Set());
    onChanged?.();
  };

  const handleDismiss = async () => {
    const ids = [...selected];
    const result = await dismiss(ids, dismissReason.trim());
    if (result.ok === false) {
      toast.error(result.error);
      return;
    }
    toast.success(t('{0} item(s) dismissed', { 0: ids.length }));
    setDismissOpen(false);
    setDismissReason('');
  };

  const handleUndo = async () => {
    if (!undoableBatchId) return;
    const result = await undo(undoableBatchId);
    if (result.ok === false) {
      toast.error(result.error);
      return;
    }
    const { reverted, skipped } = result.result;
    if (skipped.length > 0) {
      toast.warning(
        t('{0} reverted. Left in place because they changed since the import: {1}', {
          0: reverted,
          1: skipped.map((s) => s.name).join(', '),
        }),
      );
    } else {
      toast.success(t('Import undone ({0} item(s) reverted)', { 0: reverted }));
    }
    onChanged?.();
  };

  const selectedCount = proposals.filter((p) => selected.has(p.id)).length;

  const renderRow = (p: ImportProposal) => {
    const o = overrides[p.id];
    const open_ = unresolvedConflicts(p, o);
    const attention = open_.length > 0 || (p.needs_attention && Object.keys(p.conflicts).length === 0);
    const isUpdate = !!p.matched_project_id;
    const showSources = expandedSources.has(p.id);
    const sourceCount = Math.max(p.sourceCount, p.sources.length);
    // Spend, completion and health only exist on records read from a whole project dossier.
    const hasRecord = p.proposal_type === 'project' &&
      (p.fields.spent !== null || p.fields.progress !== null || p.fields.health !== null || !!p.conflicts.spent || !!p.conflicts.progress || !!p.conflicts.health);
    const meta = [
      p.fields.code,
      p.fields.manager ? t('Manager: {0}', { 0: p.fields.manager }) : null,
      p.fields.sponsor ? t('Sponsor: {0}', { 0: p.fields.sponsor }) : null,
      p.fields.status ? t('Status: {0}', { 0: p.fields.status }) : null,
      p.fields.currency ? t('Amounts in {0}', { 0: p.fields.currency }) : null,
    ].filter((m): m is string => !!m);

    const isMoney = (key: ProposalFieldKey) => key === 'budget' || key === 'spent';
    const show = (key: ProposalFieldKey, v: string | number) =>
      isMoney(key) ? currency(Number(v)) : key === 'progress' ? `${v}%` : key === 'health' ? tx(String(v).replace('-', ' ')) : String(v);
    const field = (key: ProposalFieldKey, label: string, inputType: 'text' | 'number' | 'date' | 'health', width: string) => {
      const value = effectiveValue(p, o, key);
      const conflict = key !== 'name' && key !== 'description' && CONFLICT_FIELDS.includes(key) ? p.conflicts[key] : undefined;
      const before = isUpdate ? existingValue(p, key) : null;
      const changes = isUpdate && before !== null && before !== '' && before !== 0 && String(before) !== String(value ?? '');
      return (
        <div className={width}>
          <label className="text-[11px] text-muted-foreground">{label}</label>
          {conflict && !(o && key in o) ? (
            <Select onValueChange={(v) => setField(p.id, key, v)}>
              <SelectTrigger className="h-8 text-xs border-amber-500/60">
                <SelectValue placeholder={t('Choose ({0} options)', { 0: conflict.length })} />
              </SelectTrigger>
              <SelectContent>
                {conflict.map((alt) => (
                  <SelectItem key={String(alt.value)} value={String(alt.value)}>
                    {show(key, alt.value)}
                    {' · '}
                    {t('{0} file(s)', { 0: alt.candidate_ids.length })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : inputType === 'health' ? (
            <Select value={value === null ? undefined : String(value)} onValueChange={(v) => setField(p.id, key, v)}>
              <SelectTrigger className="h-8 text-xs" aria-label={`${label} — ${p.name}`}>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {HEALTH_OPTIONS.map((h) => (
                  <SelectItem key={h} value={h}>{tx(h.replace('-', ' '))}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={inputType}
              className="h-8 text-xs"
              value={value === null ? '' : String(value)}
              onChange={(e) => setField(p.id, key, e.target.value)}
              aria-label={`${label} — ${p.name}`}
            />
          )}
          {changes && (
            <div className="text-[11px] text-amber-600 mt-0.5">
              {t('Currently {0}', { 0: show(key, before as string | number) })}
              {!(o && key in o) && ` · ${t('kept unless you edit this field')}`}
            </div>
          )}
        </div>
      );
    };

    return (
      <div key={p.id} className="rounded-md border border-border p-3 space-y-2">
        <div className="flex items-start gap-3">
          <Checkbox
            className="mt-1"
            checked={selected.has(p.id)}
            onCheckedChange={(v) => toggle(p.id, v === true)}
            aria-label={t('Select {0}', { 0: p.name })}
          />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {field('name', t('Name'), 'text', 'flex-1 min-w-[12rem]')}
              <Badge variant={isUpdate ? 'secondary' : 'default'} className="mt-4">
                {isUpdate ? t('Updates {0}', { 0: p.matched_project?.name ?? t('existing project') }) : t('New')}
              </Badge>
              <Badge variant="outline" className="mt-4">{Math.round(p.confidence * 100)}%</Badge>
              {attention && <Badge variant="destructive" className="mt-4">{t('Needs attention')}</Badge>}
            </div>
            <div className="flex flex-wrap gap-2">
              {field('budget', t('Budget'), 'number', 'w-32')}
              {field('start_date', t('Start'), 'date', 'w-36')}
              {field('end_date', t('End'), 'date', 'w-36')}
              {field('portfolio', t('Portfolio'), 'text', 'flex-1 min-w-[10rem]')}
            </div>
            {hasRecord && (
              <div className="flex flex-wrap gap-2">
                {field('spent', t('Spent'), 'number', 'w-32')}
                {field('progress', t('Complete %'), 'number', 'w-24')}
                {field('health', t('Health'), 'health', 'w-36')}
              </div>
            )}
            {meta.length > 0 && (
              <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3">
                {meta.map((m) => <span key={m}>{m}</span>)}
              </div>
            )}
            {sourceCount > 0 && (
              <button
                type="button"
                className="text-[11px] text-muted-foreground flex items-center gap-1 hover:text-foreground"
                onClick={() =>
                  setExpandedSources((prev) => {
                    const next = new Set(prev);
                    if (next.has(p.id)) next.delete(p.id);
                    else next.add(p.id);
                    return next;
                  })
                }
              >
                <FileText className="w-3 h-3" />
                {t('From {0} file(s)', { 0: sourceCount })}
                {showSources && p.sources.length > 0 && <span className="text-foreground">: {p.sources.join(', ')}</span>}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            {proposals.length > 0
              ? t('{0} item(s) from your documents — {1} ready, {2} need attention', {
                  0: proposals.length,
                  1: readyCount,
                  2: attentionCount,
                })
              : t('Recent import from your documents')}
          </CardTitle>
          <div className="flex items-center gap-2">
            {undoableBatchId && (
              <Button size="sm" variant="outline" onClick={handleUndo} disabled={busy}>
                <Undo2 className="w-3.5 h-3.5 mr-1" />
                {t('Undo last import')}
              </Button>
            )}
            {proposals.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                {open ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                {open ? t('Hide') : t('Review')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {open && proposals.length > 0 && (
        <CardContent className="space-y-4 pt-0">
          {rollup && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 rounded-md bg-muted/50 px-3 py-2 text-xs" data-testid="import-rollup">
              <span><span className="text-muted-foreground">{t('Selected')}: </span><span className="font-medium">{t('{0} project(s)', { 0: rollup.count })}</span></span>
              {rollup.portfolios > 0 && <span><span className="text-muted-foreground">{t('Portfolios')}: </span><span className="font-medium">{rollup.portfolios}</span></span>}
              {rollup.budget > 0 && <span><span className="text-muted-foreground">{t('Budget')}: </span><span className="font-medium">{currency(rollup.budget, { compact: true })}</span></span>}
              {rollup.spent > 0 && <span><span className="text-muted-foreground">{t('Spent')}: </span><span className="font-medium">{currency(rollup.spent, { compact: true })}</span></span>}
              {rollup.completion !== null && <span><span className="text-muted-foreground">{t('Complete (budget-weighted)')}: </span><span className="font-medium">{rollup.completion}%</span></span>}
              {rollup.health && (
                <span>
                  <span className="text-muted-foreground">{t('Health')}: </span>
                  <span className="font-medium">{rollup.health['on-track']} {t('on track')} · {rollup.health['at-risk']} {t('at risk')} · {rollup.health.critical} {t('off track')}</span>
                </span>
              )}
            </div>
          )}
          {sections.map((s) => (
            <div key={s.label ?? '__all__'} className="space-y-2">
              {s.label && <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{s.label}</h4>}
              {s.items.map(renderRow)}
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {type === 'project' && leftoverCount > 0 && (
                onOpenDocumentIntelligence ? (
                  <button type="button" className="underline hover:text-foreground" onClick={onOpenDocumentIntelligence}>
                    {t('{0} item(s) need manual linking in Document Intelligence', { 0: leftoverCount })}
                  </button>
                ) : (
                  <span>{t('{0} item(s) need manual linking in Document Intelligence', { 0: leftoverCount })}</span>
                )
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={busy || selectedCount === 0} onClick={() => setDismissOpen(true)}>
                {t('Dismiss selected')}
              </Button>
              <Button size="sm" disabled={busy || selectedCount === 0} onClick={handleApply}>
                {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                {t('Approve selected ({0})', { 0: selectedCount })}
              </Button>
            </div>
          </div>
        </CardContent>
      )}

      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Dismiss {0} item(s)', { 0: selectedCount })}</DialogTitle>
          </DialogHeader>
          <Textarea
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder={t('Reason (required)')}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissOpen(false)}>{t('Cancel')}</Button>
            <Button variant="destructive" disabled={busy || !dismissReason.trim()} onClick={handleDismiss}>
              {t('Dismiss')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ImportReviewStrip;
